import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  Input,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatRadioModule } from '@angular/material/radio';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { startWith } from 'rxjs';
import { ErrorFieldComponent } from '../error-field/error-field.component';

export interface RadioButton {
  name: string;
  id: string | number | boolean;
  disabled?: boolean;
  selected?: boolean;
}

/**
 * @example
 *  <siat-radio-field
 *   [formGroup]="formModificaStruttura"
 *   controlName="abilitato"
 *   label="Abilitato"
 *   id="abilitato"
 *   name="abilitatoName"
 *   data="[{label: 'Si', value: true}, {label: 'No', value: false}]"
 *   >
 *  </siat-radio-field>
 */

@UntilDestroy()
@Component({
  selector: 'siat-radio-field',
  templateUrl: './radio-field.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ErrorFieldComponent,
    MatRadioModule,
  ],
})
export class RadioFieldComponent implements ControlValueAccessor {
  /**
   * The form group the field belongs to.
   */
  formGroup = input<FormGroup>();

  /**
   * The name of the control within the form group.
   */
  controlName = input<string>();

  /**
   * The field label.
   */
  label = input<string>();

  /**
   * ID of external visible text that names this radio group.
   */
  ariaLabelledBy = input<string>();

  /**
   * The field id.
   */
  id = input<string>();
  /**
   * The field name.
   */
  name = input<string>();

  /**
   * The data value associated with the radio.
   */
  data = input<RadioButton[]>();

  /**
   * The disabled value associated with the radio.
   */
  hideMarker = input<boolean>(false);

  /**
   * Indicates if the field is required.
   */
  isRequired = computed(() => {
    return (
      this.control().hasValidator(Validators.required) && !this.hideMarker()
    );
  });

  /**
   * The style class of the field.
   */
  @Input()
  displayInline = false;

  /**
   * Indicates if the field are dynamic
   * @see DynamicFormViewerComponent.
   */
  areDynamicField = input<boolean>(false);

  /**
   * Additional class for the label.
   */
  labelClass = input<string>('');

  /**
   * @inheritdoc
   */
  onChange: (value: string | number) => void = () => {};

  /**
   * @inheritdoc
   */
  onTouched: () => void = () => {};
  constructor() {
    effect(() => {
      const isDynamic = this.areDynamicField();
      const ctrl = this.control();

      if (isDynamic && ctrl) {
        this.setupDynamicSync();
      }
    });
  }

  /**
   * Syncs the value of the control with the selected radio button.
   */
  private setupDynamicSync() {
    const ctrl = this.control();

    ctrl.valueChanges
      .pipe(untilDestroyed(this), startWith(ctrl.value))
      .subscribe(selectedId => {
        const optionsArray = this.formGroup().get('options') as FormArray;
        if (!optionsArray) return;

        optionsArray.controls.forEach(group => {
          const id = group.get('id')?.value;
          const selectedCtrl = group.get('selected');

          if (selectedCtrl) {
            selectedCtrl.setValue(id === selectedId, { emitEvent: false });
          }
        });
      });
  }

  /**
   * Gets the form control.
   */
  control = computed(() => {
    return this.formGroup().get(this.controlName()) as FormControl;
  });

  /**
   * Gets the value of the control.
   */
  get controlValue(): string | number {
    return this.control()?.value;
  }

  /**
   * A flag telling whether the control is disabled or not.
   */
  get disabled(): boolean {
    return this.control().disabled;
  }

  /**
   * @inheritdoc
   */
  writeValue(obj: string | number): void {
    this.control().setValue(obj);
  }

  /**
   * The register on change function.
   */
  registerOnChange(changeCallback: (value: string | number) => void): void {
    this.onChange = changeCallback;
  }

  /**
   * The register on touched function.
   */
  registerOnTouched(touchedCallback: () => void): void {
    this.onTouched = touchedCallback;
  }
}
